import { useState } from 'react'
import { flexRender, type Table as TanStackTable } from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'
import type { ColumnDef } from './types'

const MIN_COLUMN_WIDTH = 60

interface TableStateProps<TData extends Record<string, unknown>> {
  columns: ColumnDef<TData>[]
  table: TanStackTable<TData>
  className?: string
  title?: string
  description?: string
  buttonText?: string
  onClickButton?: () => void
}

export function TableState<TData extends Record<string, unknown>>({
  columns,
  table,
  className,
  // Defaults apply to an omitted prop and to an explicit `undefined`, which is
  // what a caller passing a conditional value sends when the condition is false.
  title = 'No data available yet.',
  description = "Once entries are added, they'll appear here automatically.",
  buttonText = 'Refresh',
  onClickButton,
}: TableStateProps<TData>) {
  const [isLoading, setIsLoading] = useState(false)
  const columnCount = Math.max(columns.length, 5)

  const handleClick = async () => {
    if (!onClickButton) return
    setIsLoading(true)
    try {
      await onClickButton()
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn('w-full flex-1 flex flex-col min-h-0', className)}>
      <div className="rounded-md flex-1 min-h-0">
        <div
          className="relative overflow-x-auto overflow-y-auto h-full pr-4 md:pr-8"
          style={{ maxHeight: 'calc(100vh - 200px)' }}
        >
          <Table disableWrapper className="min-w-full">
            <TableHeader className="sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <TableHead
                      key={header.id}
                      style={{
                        width: Math.max(header.getSize(), MIN_COLUMN_WIDTH),
                        minWidth: MIN_COLUMN_WIDTH,
                        background:
                          'color-mix(in srgb, var(--primary) 8%, var(--background))',
                      }}
                      className={cn(
                        'border-t',
                        index === 0 && 'rounded-tl-sm',
                        index === headerGroup.headers.length - 1 &&
                          'rounded-tr-sm'
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {Array.from({ length: 22 }).map((_, rowIndex) => (
                <TableRow key={rowIndex} className="hover:bg-transparent">
                  {Array.from({ length: columnCount }).map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton className="h-4 w-full opacity-50" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Overlay card - fixed to center in viewport */}
        <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-10">
          <Card className="w-[370px] pointer-events-auto border border-primary/50 rounded-md p-8 shadow-[0px_10px_22px_0px_#0000001A,0px_41px_41px_0px_#00000017,0px_92px_55px_0px_#0000000D,0px_163px_65px_0px_#00000003,0px_254px_71px_0px_#00000000]">
            <CardContent className="flex flex-col items-center p-0 text-center">
              <Typography variant="h4" className="font-bold">
                {title}
              </Typography>
              <Typography
                variant="p"
                className="mt-2 text-base font-normal text-primary"
              >
                {description}
              </Typography>
              {onClickButton && (
                <Button
                  variant="outline"
                  onClick={handleClick}
                  disabled={isLoading}
                  className="mt-4"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    buttonText
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
